import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getMatchDebug } from '../api/client';
import type { MatchDebugInfo } from '../api/types';
import ScoreBreakdown from '../components/scores/ScoreBreakdown';
import SideBySide from '../components/comparison/SideBySide';

export default function MatchDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [debug, setDebug] = useState<MatchDebugInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    getMatchDebug(id)
      .then(setDebug)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading...</div>;
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        Error: {error}
      </div>
    );
  }

  if (!debug) return null;

  const { match, storyA, storyB, mirrorAnalysis, sharedCues } = debug;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/stories" className="text-pebbl-600 hover:text-pebbl-700">
          &larr; Back
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">Match Detail</h1>
        <span className={`text-sm font-medium px-2 py-1 rounded ${
          match.status === 'confirmed'
            ? 'bg-green-100 text-green-700'
            : match.status === 'rejected'
            ? 'bg-red-100 text-red-700'
            : 'bg-yellow-100 text-yellow-700'
        }`}>
          {match.status}
        </span>
      </div>

      {/* Score Breakdown */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Score Breakdown</h2>
        <ScoreBreakdown scores={match.scores} />
      </div>

      {/* Side by Side Comparison */}
      <SideBySide storyA={storyA} storyB={storyB} />

      {/* Mirror Analysis */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Mirror Analysis</h2>
        <p className="text-sm text-gray-500 mb-4">
          A's description of "them" should match B's description of "you" (and vice versa)
        </p>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-medium text-gray-700 mb-2">A.them vs B.you</h3>
            <div className="space-y-2">
              {mirrorAnalysis.aThemVsBYou.map((item, i) => (
                <div key={i} className="text-sm border rounded p-2">
                  <div className="font-medium text-gray-600 capitalize">{item.field}</div>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    <div>
                      <div className="text-xs text-gray-400">A.them</div>
                      <div className="text-gray-700">
                        {item.aValue.length > 0 ? item.aValue.join(', ') : '-'}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-400">B.you</div>
                      <div className="text-gray-700">
                        {item.bValue.length > 0 ? item.bValue.join(', ') : '-'}
                      </div>
                    </div>
                  </div>
                  {item.overlap.length > 0 && (
                    <div className="mt-1">
                      <span className="text-xs text-green-600">
                        Match: {item.overlap.join(', ')}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="font-medium text-gray-700 mb-2">B.them vs A.you</h3>
            <div className="space-y-2">
              {mirrorAnalysis.bThemVsAYou.map((item, i) => (
                <div key={i} className="text-sm border rounded p-2">
                  <div className="font-medium text-gray-600 capitalize">{item.field}</div>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    <div>
                      <div className="text-xs text-gray-400">B.them</div>
                      <div className="text-gray-700">
                        {item.aValue.length > 0 ? item.aValue.join(', ') : '-'}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-400">A.you</div>
                      <div className="text-gray-700">
                        {item.bValue.length > 0 ? item.bValue.join(', ') : '-'}
                      </div>
                    </div>
                  </div>
                  {item.overlap.length > 0 && (
                    <div className="mt-1">
                      <span className="text-xs text-green-600">
                        Match: {item.overlap.join(', ')}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Shared Cues */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Shared Cues</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <div className="text-sm text-gray-500">Same Venue</div>
            <div className={sharedCues.venue ? 'text-green-600 font-medium' : 'text-gray-400'}>
              {sharedCues.venue ? 'Yes' : 'No'}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Time Overlap</div>
            <div className={sharedCues.timeOverlap ? 'text-green-600 font-medium' : 'text-gray-400'}>
              {sharedCues.timeOverlap ? 'Yes' : 'No'}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Event Similarity</div>
            <div className="font-medium">{(sharedCues.eventSimilarity * 100).toFixed(0)}%</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Shared Micro Cues</div>
            <div>{sharedCues.microCues.length > 0 ? sharedCues.microCues.join(', ') : '-'}</div>
          </div>
        </div>
      </div>

      {/* Explanation */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Match Explanation</h2>
        <ul className="space-y-1">
          {match.explanation.map((e, i) => (
            <li key={i} className="text-gray-700">{e}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}
